// Typed facade over the zero-dependency 2D geometric constraint solver in
// packages/gcs (@stenalpro/gcs). The solver ships as plain ESM JavaScript with
// no .d.ts; esbuild bundles it straight into the renderer, and the types the app
// relies on are declared here so the rest of the codebase stays fully typed.
//
// See packages/gcs/README.md for the full model. The app uses the authoring
// layer (buildSketch) for ring shapes plus solveSketch/analyzeSketch.

import type { Vec2 } from '../shared/types';
// @ts-ignore — plain ESM JS module (no declaration file); resolved & bundled by esbuild.
import * as gcs from '../../packages/gcs/index.js';

export type GcsEdge = [string, string]; // ordered id pair (direction b − a)

export interface GcsPoint { id: string; x: number; y: number; fixed?: boolean; }
export interface GcsConstraint {
  id: string;
  type: string;
  points?: string[];
  edges?: GcsEdge[];
  value?: number;
  /** `rigid` only: each further point's frame coordinates [u, w]. */
  local?: number[][];
}
export interface GcsSketch { points: GcsPoint[]; constraints: GcsConstraint[]; }

export interface GcsSolvedPoint { id: string; x: number; y: number; fixed: boolean; free: boolean; }
export interface GcsResult {
  ok: boolean;
  points: GcsSolvedPoint[];
  dof: number;
  gauge: number;
  shapeDefined: boolean;
  conflicting: boolean;
  residualNorm: number;
}
export interface GcsAnalysis {
  dof: number;
  gauge: number;
  shapeDefined: boolean;
  conflicting: boolean;
  residualNorm: number;
}

export interface SolveOpts {
  /** Soft pull toward a cursor for interactive dragging (hard constraints win). */
  drag?: { point: string; x: number; y: number; weight?: number };
  /** Several soft pulls at once (each like `drag`, without its warm start). */
  pulls?: Array<{ point: string; x: number; y: number; weight?: number }>;
  /** Prefer keeping the angles between these edges (absorb length edits elsewhere). */
  angleEdges?: GcsEdge[];
  /** false: return the solved points only, skipping the DOF analysis (live solves). */
  diagnostics?: boolean;
  regWeight?: number;
  angleWeight?: number;
  conflictTol?: number;
  restarts?: number;
  maxIter?: number;
}

/** Per-edge orientation / length / absolute-angle lock (authoring model). */
export interface EdgeCon { o?: 'h' | 'v' | null; len?: number | null; angle?: number | null; }
/** A relational or vertex constraint keyed by vertex/edge index (authoring model). */
export interface Relation {
  type: string;
  edges?: number[];
  points?: number[];
  verts?: number[];
  vert?: number;
  value?: number;
}
export interface BuildInput {
  pts: Array<{ x: number; y: number; fixed?: boolean }>;
  edgeCons?: EdgeCon[];
  relations?: Relation[];
  closed?: boolean;
}
export interface BuildResult { ids: string[]; sketch: GcsSketch; edges: GcsEdge[]; }

const g = gcs as {
  solveSketch: (s: GcsSketch, o?: SolveOpts) => GcsResult;
  analyzeSketch: (s: GcsSketch) => GcsAnalysis;
  wouldConflict: (s: GcsSketch, c: GcsConstraint) => { conflicting: boolean; redundant: boolean };
  buildSketch: (i: BuildInput) => BuildResult;
  cornerAngle: (pts: Vec2[], k: number) => number | null;
  REL_META: Record<string, { name: string; glyph: string; on: 'edge' | 'vertex'; valued?: boolean }>;
};

export function solveSketch(sketch: GcsSketch, opts?: SolveOpts): GcsResult { return g.solveSketch(sketch, opts); }
export function analyzeSketch(sketch: GcsSketch): GcsAnalysis { return g.analyzeSketch(sketch); }
export function wouldConflict(sketch: GcsSketch, c: GcsConstraint): { conflicting: boolean; redundant: boolean } { return g.wouldConflict(sketch, c); }
export function buildSketch(input: BuildInput): BuildResult { return g.buildSketch(input); }
export function cornerAngle(pts: Vec2[], k: number): number | null { return g.cornerAngle(pts, k); }
export const REL_META = g.REL_META;
